import DetailMixinController from '@/mixins/DetailMixinController';
import NetworkService from '@/services/NetworkService';
import UserService from '@/services/UserService';

const subscriptionStatus = {
  incomplete: 'Problema de pagamento',
  incomplete_expired: 'Cancelado (expirado)',
  trialing: 'Período de teste',
  active: 'Ativo',
  past_due: 'Pagamento atrasado',
  canceled: 'Cancelado',
  unpaid: 'Não pago',
  paused: 'Pausado',
};

const periodLabel = {
  month: 'Mensal',
  semester: 'Semestral',
  year: 'Anual',
};

const invoiceStatus = {
  uncollectible: 'Cobrança não aplicável',
  draft: 'Rascunho',
  open: 'Em aberto',
  void: 'Cancelado',
  paid: 'Pago',
};

const nfStatusLabels = {
  sent_by_email: 'Emitido',
  issued: 'Emitido',
  cancelled: 'Cancelado',
  processing: 'Processando',
};

const nfStatusClasses = {
  sent_by_email: 'bg-success',
  issued: 'bg-success',
  cancelled: 'bg-danger',
  processing: 'bg-warning text-dark',
};

const refundStatusLabels = {
  succeeded: 'Reembolsado',
  pending: 'Reembolso pendente',
  failed: 'Reembolso falhou',
  canceled: 'Reembolso cancelado',
};

const refundStatusClasses = {
  succeeded: 'bg-info text-dark',
  pending: 'bg-warning text-dark',
  failed: 'bg-danger',
  canceled: 'bg-secondary',
};

function translateInvoiceDescription(desc) {
  if (!desc) return desc;
  let out = desc
    .replace(/\(at /gi, '(')
    .replace(/ \/ year\)/gi, ' / ano)')
    .replace(/ \/ month\)/gi, ' / mês)')
    .replace(/ \/ week\)/gi, ' / semana)')
    .replace(/ \/ day\)/gi, ' / dia)')
    .replace(/ \/ semester\)/gi, ' / semestre)')
    .replace(/Time\s?-\s?off/gi, 'Período sem cobrança')
    .replace(/Unused time/gi, 'Tempo não utilizado')
    .replace(/Remaining time/gi, 'Tempo restante')
    .replace(/Trial period/gi, 'Período de teste')
    .replace(/ on /gi, ' em ');

  out = out.replace(
    /R\$\s?(\d{1,3}(?:,\d{3})*(?:\.\d{1,2})?)/g,
    (match, num) => {
      const [intPart, decPart] = num.split('.');
      const intFormatted = intPart.replace(/,/g, '.');
      return decPart ? `R$ ${intFormatted},${decPart}` : `R$ ${intFormatted}`;
    }
  );

  return out;
}

export default {
  name: 'SubscriptionDetail',
  mixins: [DetailMixinController],
  data() {
    return {
      network: new NetworkService(),
      planLabels: new UserService().planLabels,
      // Modals
      showGrantDaysModal: false,
      showGrantFreeMonthModal: false,
      showGrantCreditModal: false,
      showCancelModal: false,
      confirmCancel: false,
      submittingCancel: false,
      // Refund
      showRefundModal: false,
      refundForm: {
        invoice: null,
        reason: '',
      },
      submittingRefund: false,
      // Forms
      grantDaysForm: {
        days: 7,
      },
      grantFreeMonthForm: {
        duration_in_months: 1,
      },
      grantCreditForm: {
        amount: null,
        description: '',
      },
      // Submitting states
      submittingGrantDays: false,
      submittingGrantFreeMonth: false,
      submittingGrantCredit: false,
    };
  },
  computed: {
    isPlusPlan() {
      return this.item.plan?.slug === 'psiapp-mais-pro';
    },
    canGrantDays() {
      return ['active', 'trialing'].includes(this.item.status);
    },
    canGrantFreeMonth() {
      return ['active', 'trialing', 'past_due'].includes(this.item.status);
    },
    canGrantCredit() {
      return this.item.status === 'active' && this.item.user?.customer_id;
    },
    canCancelSubscription() {
      return (
        ['active', 'trialing', 'past_due'].includes(this.item.status) &&
        !this.item.canceled_at
      );
    },
    creditExceedsSubscription() {
      if (!this.grantCreditForm.amount || !this.item.unit_amount) return false;
      const creditInCents = this.grantCreditForm.amount * 100;
      return creditInCents > this.item.unit_amount;
    },
  },
  methods: {
    fetchPromise() {
      return this.network.get(`api/subscriptions/${this.id}`);
    },
    prepareResponse(data) {
      // Format status
      let formatted_status = subscriptionStatus[data.status] || data.status || '---';
      if (
        data.status === 'canceled' &&
        data.trial_end &&
        this.$moment(data.trial_end).isAfter(this.$moment())
      ) {
        formatted_status = `Cortesia até ${this.$moment(data.trial_end).format('DD/MM/YYYY')}`;
      } else if (data.status === 'active' && data.canceled_at) {
        formatted_status = `Ativo até ${this.$moment(data.cancel_at).format('DD/MM/YYYY')}`;
      } else if (data.status === 'trialing' && data.trial_end) {
        formatted_status = `Período de teste até ${this.$moment(data.trial_end).format('DD/MM/YYYY')}`;
      }

      // Format plan frequency
      let formatted_plan_frequency = '---';
      if (data.plan?.slug && this.planLabels[data.plan.slug]) {
        const planName = this.planLabels[data.plan.slug];
        const frequency = data.price?.frequency ? ` ${periodLabel[data.price.frequency]}` : '';
        formatted_plan_frequency = `${planName}${frequency}`;
      }

      // Format invoices (API returns all_invoices)
      let invoices = data.all_invoices || data.invoices || [];
      invoices = invoices.map((inv) => {
        const fmtBrl = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
        const creditNotesTotal = inv.credit_notes_total || 0;
        const hasCreditNotes = creditNotesTotal > 0;
        const zeroedByCredit = !!inv.zeroed_by_credit_note;
        // Partial credit = some credit applied but invoice not fully zeroed
        const partialCredit = hasCreditNotes && !zeroedByCredit;

        // Nota Fiscal
        let nf = null;
        if (inv.invoice) {
          nf = {
            ...inv.invoice,
            formatted_status: nfStatusLabels[inv.invoice.invoice_status] || inv.invoice.invoice_status || '-',
            status_class: nfStatusClasses[inv.invoice.invoice_status] || 'bg-secondary',
            formatted_issue_date: inv.invoice.invoice_issue_date
              ? this.$moment(inv.invoice.invoice_issue_date).format('DD/MM/YYYY')
              : null,
            formatted_cancel_date: inv.invoice.invoice_cancel_date
              ? this.$moment(inv.invoice.invoice_cancel_date).format('DD/MM/YYYY')
              : null,
          };
        }

        // Refund
        const amountRefunded = parseFloat(inv.amount_refunded) || 0;
        const amountPaid = parseFloat(inv.amount_paid) || 0;
        const hasRefund = !!inv.refund_id || !!inv.refund_status;
        const isFullyRefunded =
          inv.refund_status === 'succeeded' &&
          amountRefunded > 0 &&
          amountPaid > 0 &&
          amountRefunded >= amountPaid;
        let refund = null;
        if (hasRefund) {
          refund = {
            id: inv.refund_id,
            status: inv.refund_status,
            amount: amountRefunded,
            reason: inv.refund_reason,
            refunded_at: inv.refunded_at,
            refunded_by_user_id: inv.refunded_by_user_id,
            formatted_status: refundStatusLabels[inv.refund_status] || inv.refund_status || '-',
            status_class: refundStatusClasses[inv.refund_status] || 'bg-secondary',
            formatted_amount: fmtBrl(amountRefunded),
            formatted_date: inv.refunded_at
              ? this.$moment(inv.refunded_at).format('DD/MM/YYYY HH:mm')
              : null,
            is_full: isFullyRefunded,
            is_partial:
              inv.refund_status === 'succeeded' &&
              amountRefunded > 0 &&
              !isFullyRefunded,
          };
        }
        const canRefund =
          inv.status === 'paid' &&
          amountPaid > 0 &&
          (!inv.refund_status || ['failed', 'canceled'].includes(inv.refund_status));

        return {
          ...inv,
          formatted_created: inv.created
            ? this.$moment(inv.created).format('DD/MM/YYYY')
            : '-',
          formatted_status: inv.status ? invoiceStatus[inv.status] : '',
          formatted_total: inv.total != null ? fmtBrl(inv.total) : '-',
          formatted_amount_paid: inv.amount_paid != null ? fmtBrl(inv.amount_paid) : null,
          formatted_credit_notes_total: hasCreditNotes ? fmtBrl(creditNotesTotal) : null,
          has_credit_notes: hasCreditNotes,
          zeroed_by_credit_note: zeroedByCredit,
          partial_credit: partialCredit,
          nf,
          refund,
          can_refund: canRefund,
        };
      });

      // Format upcoming invoice
      let upcoming_invoice = null;
      if (data.upcoming_invoice) {
        const ui = data.upcoming_invoice;
        const currency = (ui.currency || 'brl').toUpperCase() === 'BRL' ? 'BRL' : 'USD';
        const fmt = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency });

        const lines = (ui.lines || []).map((l) => ({
          ...l,
          description: translateInvoiceDescription(l.description),
          is_credit: (l.amount || 0) < 0,
          formatted_amount: fmt(l.amount),
        }));
        const credits = (ui.credits || []).map((c) => ({
          ...c,
          description: translateInvoiceDescription(c.description),
          formatted_amount: fmt(c.amount),
        }));

        upcoming_invoice = {
          ...ui,
          lines,
          credits,
          formatted_date: ui.next_payment_attempt
            ? this.$moment(ui.next_payment_attempt).format('DD/MM/YYYY')
            : (ui.period_end ? this.$moment(ui.period_end).format('DD/MM/YYYY') : '-'),
          formatted_amount: fmt(ui.amount_due),
          formatted_subtotal: fmt(ui.subtotal),
          formatted_total: fmt(ui.total),
        };
      }

      return {
        ...data,
        formatted_status,
        formatted_plan_frequency,
        invoices,
        upcoming_invoice,
        formatted_start_date: data.start_date
          ? this.$moment(data.start_date).format('DD/MM/YYYY')
          : '-',
        formatted_canceled_at: data.canceled_at
          ? this.$moment(data.canceled_at).format('DD/MM/YYYY')
          : '---',
        formatted_current_period_start: data.current_period_start
          ? this.$moment(data.current_period_start).format('DD/MM/YYYY')
          : '-',
        formatted_current_period_end: data.current_period_end
          ? this.$moment(data.current_period_end).format('DD/MM/YYYY')
          : '-',
        formatted_created_at: data.created_at
          ? this.$moment(data.created_at).format('DD/MM/YYYY HH:mm')
          : '-',
        formatted_unit_amount: data.unit_amount
          ? (data.unit_amount / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
          : '-',
      };
    },
    stripeSubscriptionLink() {
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid = process.env.VUE_APP_ENV === 'prod' ? 'subscriptions' : 'test/subscriptions';
      return `https://dashboard.stripe.com/${acctId}/${mid}/${this.item.subscription_id}`;
    },
    getInvoiceLink(invoice) {
      if (invoice.hosted_invoice_url) {
        return invoice.hosted_invoice_url;
      }
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid = process.env.VUE_APP_ENV === 'prod' ? 'invoices' : 'test/invoices';
      return `https://dashboard.stripe.com/${acctId}/${mid}/${invoice.invoice_id}`;
    },
    getCustomerStripeLink(customerId) {
      const acctId = process.env.VUE_APP_STRIPE_PLATFORM_ACCOUNT_ID;
      const mid = process.env.VUE_APP_ENV === 'prod' ? 'customers' : 'test/customers';
      return `https://dashboard.stripe.com/${acctId}/${mid}/${customerId}`;
    },
    statusBadgeClass() {
      const status = this.item.status;
      if (
        status === 'canceled' &&
        this.item.trial_end &&
        this.$moment(this.item.trial_end).isAfter(this.$moment())
      ) {
        return 'bg-primary';
      }
      if (status === 'active' && this.item.canceled_at) {
        return 'bg-warning text-dark';
      }
      const classes = {
        active: 'bg-success',
        trialing: 'bg-primary',
        canceled: 'bg-danger',
        past_due: 'bg-warning text-dark',
        unpaid: 'bg-warning text-dark',
        paused: 'bg-secondary',
        incomplete: 'bg-warning text-dark',
        incomplete_expired: 'bg-danger',
      };
      return classes[status] || 'bg-secondary';
    },
    // Modal helpers
    closeGrantDaysModal() {
      this.showGrantDaysModal = false;
      this.grantDaysForm.days = 7;
    },
    closeGrantFreeMonthModal() {
      this.showGrantFreeMonthModal = false;
      this.grantFreeMonthForm.duration_in_months = 1;
    },
    closeGrantCreditModal() {
      this.showGrantCreditModal = false;
      this.grantCreditForm.amount = null;
      this.grantCreditForm.description = '';
    },
    closeCancelModal() {
      this.showCancelModal = false;
      this.confirmCancel = false;
    },
    refundIconClass(refund) {
      if (refund.status === 'succeeded') return 'text-info';
      if (refund.status === 'pending') return 'text-warning';
      if (refund.status === 'failed') return 'text-danger';
      return 'text-muted';
    },
    refundTextClass(refund) {
      if (refund.status === 'succeeded') return 'text-info';
      if (refund.status === 'pending') return 'text-warning';
      if (refund.status === 'failed') return 'text-danger';
      return 'text-muted';
    },
    openRefundModal(invoice) {
      this.refundForm.invoice = invoice;
      this.refundForm.reason = '';
      this.showRefundModal = true;
    },
    closeRefundModal() {
      this.showRefundModal = false;
      this.refundForm.invoice = null;
      this.refundForm.reason = '';
    },
    async handleRefund() {
      if (!this.refundForm.invoice) return;
      this.submittingRefund = true;
      try {
        await this.network.post(
          `api/subscription-invoices/${this.refundForm.invoice.id}/refund`,
          {
            reason: this.refundForm.reason || undefined,
          }
        );
        const amount =
          this.refundForm.invoice.formatted_amount_paid ||
          this.refundForm.invoice.formatted_total;
        this.$eventBus.$emit('showToast', {
          title: 'Reembolso solicitado',
          msg: `Reembolso de ${amount} processado com sucesso.`,
        });
        this.closeRefundModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao processar reembolso.',
        });
      } finally {
        this.submittingRefund = false;
      }
    },
    async handleCancelSubscription() {
      this.submittingCancel = true;
      try {
        await this.network.delete(`api/subscriptions/${this.item.id}`);
        this.$eventBus.$emit('showToast', {
          title: 'Assinatura cancelada',
          msg: 'A assinatura foi cancelada com sucesso.',
        });
        this.closeCancelModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao cancelar assinatura.',
        });
      } finally {
        this.submittingCancel = false;
      }
    },
    getNewTrialEndDate(days) {
      const baseDate = this.item.trial_end
        ? this.$moment(this.item.trial_end).clone()
        : this.$moment();
      return baseDate.add(days, 'days').format('DD/MM/YYYY');
    },
    formatCurrency(value) {
      return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    },
    // API calls
    async handleGrantDays() {
      this.submittingGrantDays = true;
      try {
        await this.network.post(
          `api/subscriptions/${this.item.id}/grant-days`,
          { days: this.grantDaysForm.days }
        );
        this.$eventBus.$emit('showToast', {
          title: 'Dias grátis concedidos',
          msg: `${this.grantDaysForm.days} dias grátis foram adicionados à assinatura.`,
        });
        this.closeGrantDaysModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao conceder dias grátis.',
        });
      } finally {
        this.submittingGrantDays = false;
      }
    },
    async handleGrantFreeMonth() {
      this.submittingGrantFreeMonth = true;
      try {
        await this.network.post(
          `api/subscriptions/${this.item.id}/grant-free-month`,
          { duration_in_months: this.grantFreeMonthForm.duration_in_months }
        );
        const months = this.grantFreeMonthForm.duration_in_months;
        this.$eventBus.$emit('showToast', {
          title: 'Mês grátis concedido',
          msg: `${months} ${months > 1 ? 'meses grátis foram aplicados' : 'mês grátis foi aplicado'} à assinatura.`,
        });
        this.closeGrantFreeMonthModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao conceder mês grátis.',
        });
      } finally {
        this.submittingGrantFreeMonth = false;
      }
    },
    async handleGrantCredit() {
      this.submittingGrantCredit = true;
      try {
        const amountInCents = Math.round(this.grantCreditForm.amount * 100);
        await this.network.post(
          `api/users/${this.item.user_id}/grant-credit`,
          {
            amount: amountInCents,
            currency: 'brl',
            description: this.grantCreditForm.description || undefined,
          }
        );
        this.$eventBus.$emit('showToast', {
          title: 'Crédito concedido',
          msg: `Crédito de ${this.formatCurrency(this.grantCreditForm.amount)} foi adicionado para ${this.item.user?.name}.`,
        });
        this.closeGrantCreditModal();
        this.getData();
      } catch (error) {
        this.$eventBus.$emit('showToast', {
          title: 'Erro',
          msg: error.message || 'Erro ao conceder crédito.',
        });
      } finally {
        this.submittingGrantCredit = false;
      }
    },
  },
};
